// Libraries
import React from 'react';

// Supermove

import {Icon, Link, Loading, Space, Styled} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {useDrawer, useModal, useResponsive, useToast} from '@supermove/hooks';
import {LeadProviderEndpoint, LeadProviderGlobalIntegration, UserModel} from '@supermove/models';
import {Typography, colors} from '@supermove/styles';
import {Datetime} from '@supermove/utils';

// App
import Badge from '@shared/design/components/Badge';
import Button from '@shared/design/components/Button';
import Callout from '@shared/design/components/Callout';
import Table from '@shared/design/components/Table';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import PageLoadingIndicator from '@shared/modules/App/components/PageLoadingIndicator';
import CompanySettingsPage from 'modules/Organization/Settings/Company/components/CompanySettingsPage';
import {CreateLeadProviderEndpointModal} from 'modules/Organization/Settings/Company/components/LeadProvider/CreateLeadProviderEndpointModal';
import {LeadProviderRequestStatus} from 'modules/Organization/Settings/Company/components/LeadProvider/LeadProviderRequestStatus';
import UpdateLeadProviderEndpointDrawer from 'modules/Organization/Settings/Company/components/LeadProvider/UpdateLeadProviderEndpointDrawer';
import UpdateLeadProviderEndpointStatusModal from 'modules/Organization/Settings/Company/components/LeadProvider/UpdateLeadProviderEndpointStatusModal';
import UpdateLeadProviderEndpointUrlDrawer from 'modules/Organization/Settings/Company/components/LeadProvider/UpdateLeadProviderEndpointUrlDrawer';

const LinkButton = Styled.ButtonV2`
`;

const LinkText = Styled.Text`
  ${Typography.Link}
`;

const EmptyText = Styled.Text`
  ${Typography.Responsive.Body}
  color: ${colors.gray.tertiary};
  font-style: italic;
`;

const Row = Styled.View``;

const hasDestination = (endpoint: LeadProviderEndpoint): boolean => {
  return endpoint.inboundWebhookEndpoint.inboundWebhookDestinations.length > 0;
};

const Content = ({
  viewer,
  endpoints,
  refetch,
}: {
  viewer: UserModel;
  endpoints: LeadProviderEndpoint[];
  refetch: ReturnType<typeof useQuery>['refetch'];
}) => {
  const responsive = useResponsive();
  const copiedToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Webhook URL copied.',
  });
  const {viewingOrganization, role} = viewer;

  const columnDefinitions = [
    {
      headerLabel: 'Status',
      width: 80,
      cellComponent: ({inboundWebhookEndpoint}: LeadProviderEndpoint) => (
        <Badge
          isResponsive
          label={
            inboundWebhookEndpoint.status[0].toUpperCase() +
            inboundWebhookEndpoint.status.substring(1)
          }
          color={
            inboundWebhookEndpoint.status === 'active' ? colors.green.status : colors.gray.secondary
          }
          style={{marginVertical: 2}}
        />
      ),
      mobileOptions: {
        isInHeader: true,
        rank: 1,
      },
    },
    ...(responsive.desktop
      ? [
          {
            headerLabel: 'Name',
            flex: 5,
            cellComponent: ({inboundWebhookEndpoint}: LeadProviderEndpoint) => (
              <Link to={`/settings/company/lead-providers/${inboundWebhookEndpoint.uuid}/leads`}>
                <LinkText>{inboundWebhookEndpoint.name}</LinkText>
              </Link>
            ),
            secondary: {
              headerLabel: 'Description',
              cellText: ({inboundWebhookEndpoint}: LeadProviderEndpoint) => {
                return inboundWebhookEndpoint.description;
              },
            },
          },
        ]
      : [
          {
            headerLabel: 'Name',
            flex: 3,
            cellText: ({inboundWebhookEndpoint}: LeadProviderEndpoint) =>
              inboundWebhookEndpoint.name,
            mobileOptions: {
              isInHeader: true,
              rank: 0,
            },
          },
          {
            headerLabel: 'Description',
            flex: 3,
            cellText: ({inboundWebhookEndpoint}: LeadProviderEndpoint) =>
              inboundWebhookEndpoint.description,
          },
        ]),
    {
      headerLabel: 'Provider',
      flex: 2,
      cellText: ({leadProviderGlobalIntegration}: LeadProviderEndpoint) =>
        leadProviderGlobalIntegration ? leadProviderGlobalIntegration.name : 'Custom',
    },
    {
      headerLabel: 'Webhook URL',
      width: 120,
      cellComponent: ({inboundWebhookEndpoint}: LeadProviderEndpoint) => (
        <LinkButton
          onPress={() => {
            navigator.clipboard.writeText(inboundWebhookEndpoint.url);
            copiedToast.handleToast();
          }}
        >
          <LinkText>{'Copy URL'}</LinkText>
        </LinkButton>
      ),
    },
    {
      headerLabel: 'Last Request',
      width: 150,
      cellComponent: ({inboundWebhookEndpoint}: LeadProviderEndpoint) => {
        const {
          summary: {latestRequest},
        } = inboundWebhookEndpoint;
        if (!latestRequest) {
          return <EmptyText>{'None'}</EmptyText>;
        }
        return (
          <Link to={`/settings/company/lead-providers/${inboundWebhookEndpoint.uuid}/logs`}>
            <LinkText>
              {Datetime.toDisplayTime(
                Datetime.fromDatetime(latestRequest.createdAt),
                Datetime.DISPLAY_DATETIME,
                viewingOrganization.timezone,
              )}
            </LinkText>
          </Link>
        );
      },
      secondary: {
        cellComponent: ({inboundWebhookEndpoint}: LeadProviderEndpoint) => {
          const status = LeadProviderRequestStatus.fromRequest(
            inboundWebhookEndpoint?.summary.latestRequest,
          );
          if (!status) {
            return <EmptyText>{'None'}</EmptyText>;
          }
          return <Badge label={status.name} color={status.color} />;
        },
      },
    },
    {
      headerLabel: 'Leads',
      width: 80,
      cellComponent: (leadProviderEndpoint: LeadProviderEndpoint) => (
        <Link
          to={`/settings/company/lead-providers/${leadProviderEndpoint.inboundWebhookEndpoint.uuid}/leads`}
        >
          <LinkText>{leadProviderEndpoint.summary.leads}</LinkText>
        </Link>
      ),
    },
    {
      headerLabel: 'Updated At',
      flex: 2,
      cellText: ({inboundWebhookEndpoint}: LeadProviderEndpoint) =>
        Datetime.toDisplayTime(
          Datetime.fromDatetime(inboundWebhookEndpoint.updatedAt),
          Datetime.DISPLAY_DATETIME,
          viewingOrganization.timezone,
        ),
      secondary: {
        cellText: ({inboundWebhookEndpoint}: LeadProviderEndpoint) =>
          inboundWebhookEndpoint.updatedByUser.fullName,
      },
    },
    {
      flex: 1,
      cellStyle: {alignItems: 'center'},
      actions: (leadProviderEndpoint: LeadProviderEndpoint) => [
        ...(!responsive.desktop
          ? [
              {
                text: 'View',
                onPress: ({handleOpen}: {handleOpen: () => void}) =>
                  window.open(
                    `/settings/company/lead-providers/${leadProviderEndpoint.inboundWebhookEndpoint.uuid}/leads`,
                  ),
              },
            ]
          : []),
        {
          text: 'Edit',
          onPress: ({handleOpen}: {handleOpen: () => void}) => handleOpen(),
          actionHook: {
            hook: useDrawer,
            hookArgument: {
              name: 'Update Lead Provider Endpoint Drawer',
            },
            renderComponent: ({
              isOpen,
              handleClose,
              hookKey,
            }: {
              isOpen: boolean;
              handleClose: () => void;
              hookKey: string;
            }) => {
              return (
                <React.Fragment>
                  {isOpen && (
                    <UpdateLeadProviderEndpointDrawer
                      key={hookKey}
                      endpoint={leadProviderEndpoint}
                      projectTypes={viewer.viewingOrganization.projectTypes}
                      isOpen={isOpen}
                      handleClose={handleClose}
                      refetch={refetch}
                    />
                  )}
                </React.Fragment>
              );
            },
          },
        },
        ...(!leadProviderEndpoint.leadProviderGlobalIntegration?.id && viewer.role === 'super'
          ? [
              {
                text: 'Edit Zapier URL',
                onPress: ({handleOpen}: {handleOpen: () => void}) => handleOpen(),
                actionHook: {
                  hook: useDrawer,
                  hookArgument: {
                    name: 'Update Lead Provider Endpoint URL Drawer',
                  },
                  renderComponent: ({
                    isOpen,
                    handleClose,
                    hookKey,
                  }: {
                    isOpen: boolean;
                    handleClose: () => void;
                    hookKey: string;
                  }) => {
                    return (
                      <React.Fragment>
                        {isOpen && (
                          <UpdateLeadProviderEndpointUrlDrawer
                            key={hookKey}
                            endpoint={leadProviderEndpoint}
                            isOpen={isOpen}
                            handleClose={handleClose}
                            refetch={refetch}
                          />
                        )}
                      </React.Fragment>
                    );
                  },
                },
              },
            ]
          : []),
        ...(leadProviderEndpoint.inboundWebhookEndpoint.status === 'inactive'
          ? [
              {
                text: 'Activate',
                onPress: ({handleOpen}: {handleOpen: () => void}) => handleOpen(),
                isDisabled: !hasDestination(leadProviderEndpoint),
                tooltip:
                  !hasDestination(leadProviderEndpoint) &&
                  'Cannot activate integration until configured by implementation manager.',
                actionHook: {
                  hook: useModal,
                  hookArgument: {
                    name: 'Activate Lead Provider Endpoint Modal',
                  },
                  renderComponent: ({
                    isOpen,
                    handleClose,
                    hookKey,
                  }: {
                    isOpen: boolean;
                    handleClose: () => void;
                    hookKey: string;
                  }) => {
                    return (
                      <React.Fragment>
                        {isOpen && (
                          <UpdateLeadProviderEndpointStatusModal
                            key={hookKey}
                            endpoint={leadProviderEndpoint}
                            isOpen={isOpen}
                            handleClose={handleClose}
                            refetch={refetch}
                          />
                        )}
                      </React.Fragment>
                    );
                  },
                },
              },
            ]
          : []),
        ...(leadProviderEndpoint.inboundWebhookEndpoint.status === 'active'
          ? [
              {
                text: 'Deactivate',
                onPress: ({handleOpen}: {handleOpen: () => void}) => handleOpen(),
                color: colors.red.warning,
                actionHook: {
                  hook: useModal,
                  hookArgument: {
                    name: 'Deactivate Lead Provider Endpoint Modal',
                  },
                  renderComponent: ({
                    isOpen,
                    handleClose,
                    hookKey,
                  }: {
                    isOpen: boolean;
                    handleClose: () => void;
                    hookKey: string;
                  }) => {
                    return (
                      <React.Fragment>
                        {isOpen && (
                          <UpdateLeadProviderEndpointStatusModal
                            key={hookKey}
                            endpoint={leadProviderEndpoint}
                            isOpen={isOpen}
                            handleClose={handleClose}
                            refetch={refetch}
                          />
                        )}
                      </React.Fragment>
                    );
                  },
                },
              },
            ]
          : []),
      ],
    },
  ];

  if (!viewer.viewingOrganization.isOwnerOfSettings) {
    return (
      <React.Fragment>
        <Row style={{width: 'fit-content', marginBottom: '16px'}}>
          <Callout
            text={
              'You are viewing a branch. Please contact the admins at the main branch to make changes.'
            }
          />
        </Row>
        <Table
          items={[]}
          itemKey={'id'}
          columnDefinitions={columnDefinitions}
          hasBorder
          emptyStateText={'No lead providers to display.'}
        />
      </React.Fragment>
    );
  }

  return (
    <Table
      items={endpoints}
      itemKey={'id'}
      columnDefinitions={columnDefinitions}
      hasBorder
      emptyStateText={'No lead providers added yet.'}
    />
  );
};

interface Response {
  viewer: UserModel;
  leadProviderGlobalIntegrations: LeadProviderGlobalIntegration[];
}

const OrganizationSettingsCompanyLeadProvidersPage = () => {
  const responsive = useResponsive();
  const {loading, data, refetch} = useQuery<Response>(
    OrganizationSettingsCompanyLeadProvidersPage.query,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const addLeadProviderModal = useModal({name: 'Add Lead Provider Modal'});

  return (
    <CompanySettingsPage
      title={'Lead Providers'}
      description={'Create and manage your lead providers.'}
    >
      <Loading loading={loading} as={PageLoadingIndicator}>
        {() => (
          <React.Fragment>
            <Button
              onPress={addLeadProviderModal.handleOpen}
              iconLeft={Icon.Plus}
              text={'Add Lead Provider'}
              style={{marginLeft: responsive.desktop ? 0 : 16}}
              isDisabled={!data?.viewer.viewingOrganization.isOwnerOfSettings}
            />
            <Space height={24} />
            <Content
              viewer={data!.viewer}
              endpoints={data!.viewer.viewingOrganization.leadProviderEndpoints}
              refetch={refetch}
            />
            {addLeadProviderModal.isOpen && (
              <CreateLeadProviderEndpointModal
                isOpen={addLeadProviderModal.isOpen}
                handleClose={addLeadProviderModal.handleClose}
                refetch={refetch}
                data={data!}
              />
            )}
          </React.Fragment>
        )}
      </Loading>
    </CompanySettingsPage>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
OrganizationSettingsCompanyLeadProvidersPage.query = gql`
    ${CreateLeadProviderEndpointModal.fragment}
    ${UpdateLeadProviderEndpointDrawer.fragment}
    ${UpdateLeadProviderEndpointStatusModal.fragment}
    ${UpdateLeadProviderEndpointUrlDrawer.fragment}
    query OrganizationSettingsCompanyLeadProvidersPage {
        ${gql.query}
        ...LeadProviderEndpointModal
        viewer {
            id
            role
            viewingOrganization {
                ...UpdateLeadProviderEndpointDrawer
                id
                isOwnerOfSettings
                timezone
                leadProviderEndpoints {
                    ...UpdateLeadProviderEndpointStatusModal
                    ...UpdateLeadProviderEndpointUrlDrawer
                    id
                    type
                    leadProviderGlobalIntegration {
                      id
                      name
                    }
                    summary {
                        leads
                        latestLeadAt
                    }
                    inboundWebhookEndpoint {
                        id
                        uuid
                        updatedAt
                        name
                        description
                        status
                        url
                        updatedByUser {
                          id
                          fullName
                        }
                        inboundWebhookDestinations {
                          id
                        }
                        summary {
                            latestRequest {
                                id
                                createdAt
                                uuid
                                mostRecentDelivery {
                                    id
                                    status
                                    isExpired
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

export default OrganizationSettingsCompanyLeadProvidersPage;
